import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { atlasContext, atlasProposals, type AtlasContext, type AtlasProposalsResponse } from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { facilitiesParam, useCompanyWideView, usePickedIds } from '../../session/FacilityScope';
import { usePageTab } from '../../shell/Shell';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { ATLAS_TABS, EASE, atlasRoleOf, tabAllowed, useShellWidth, type AtlasTab } from './common';
import { ChatTab } from './ChatTab';
import { ScribeTab } from './ScribeTab';
import { ActionsTab } from './ActionsTab';
import { AutonomyTab } from './AutonomyTab';
import { EvalTab } from './EvalTab';
import { RichCompanion } from '../companion/RichCompanion';
import { ReadOnlyNote } from '../../ui/ReadOnly';
import { TabMore } from '../../ui/TabMore';
import '../companion/companion.css';

/** V9: Chat, Conversations, Scribe and Actions stay in the row; the rest sit under More. */
export const ATLAS_KEEP: readonly AtlasTab[] = ['Chat', 'Conversations', 'Scribe', 'Actions'];
// design atTabs style
const atlasTab = (on: boolean) =>
  'display:inline-flex;align-items:center;height:30px;padding:0 14px;border-radius:7px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' +
  (on ? 'color:#FCFAF4;background:#2F5C97;' : 'color:#3C454F;background:transparent;');

/**
 * Atlas, the in-console assistant (design: isAtlas, T13041-13608; atlasVals L32023,
 * atlasChat L31196, scribeVals L31864). Chat for every role that opens Atlas; Conversations
 * (V6: residents' conversations, the retired Atlas's staff surface) for Care Staff,
 * Facility Admin, Aithora and the Partner (read only); Scribe for
 * Care Staff, Facility Admin and Aithora; Actions and Autonomy for Facility Admin and
 * Aithora; Eval for Aithora. Tabs a role cannot use are absent, not emptied.
 */
export function Atlas() {
  const { role } = useConsoleRole();
  const { client } = useSession();
  const ar = atlasRoleOf(role);
  const [params, setParams] = useSearchParams();
  const asked = ATLAS_TABS.find((t) => t.toLowerCase() === (params.get('tab') ?? '').toLowerCase()) ?? 'Chat';
  const [tabState, setTab] = useState<AtlasTab>(asked);
  // Sales has Atlas for its own questions (Andrew, 2026-10-13), not the care team's tabs: Chat only.
  const allowed = (t: AtlasTab) => tabAllowed(t, ar) && (role !== 'SALES' || t === 'Chat');
  const tab: AtlasTab = allowed(tabState) ? tabState : 'Chat';
  usePageTab(tab);
  const W = useShellWidth();
  const stacked = W < 820;
  const [railPref, setRailPref] = useState<boolean | null>(null);
  const railOpen = W >= 1120 ? railPref !== false : railPref === true;

  const ctx = useQuery<AtlasContext>((signal) => atlasContext(client, { signal }), [client]);
  // The facility switcher (V6, design geoLayer): Conversations, Scribe, Actions and Autonomy
  // follow the pick; Chat and Eval are company-wide, and the switcher says so.
  useCompanyWideView(tab === 'Chat' || tab === 'Eval');
  const facilities = facilitiesParam(usePickedIds());
  // The Actions tab carries its pending count ("Actions · 3").
  const canActions = allowed('Actions');
  const proposals = useQuery<AtlasProposalsResponse>((signal) => atlasProposals(client, { query: facilities ? { facilities } : undefined, signal }), [client, facilities], { enabled: canActions });
  const pending = (proposals.data?.proposals ?? []).filter((p) => p.status === 'pending').length;

  const pick = (t: AtlasTab) => {
    setTab(t);
    const next = new URLSearchParams(params);
    if (t === 'Chat') next.delete('tab');
    else next.set('tab', t.toLowerCase());
    setParams(next, { replace: true });
  };

  const tabs: [AtlasTab, string][] = (
    [
      ['Chat', 'Chat'],
      ['Conversations', 'Conversations'],
      ['Scribe', 'Scribe'],
      ['Actions', pending > 0 ? 'Actions · ' + pending : 'Actions'],
      ['Autonomy', 'Autonomy'],
      ['Eval', 'Eval'],
    ] as [AtlasTab, string][]
  ).filter((t) => allowed(t[0]));

  // the drawer only exists on the chat tab, so its control is absent elsewhere
  const railBtn = tab === 'Chat' && W < 1120 && !stacked;

  return (
    <div data-testid="atlas-page" style={sx("font-family:'Archivo',system-ui,sans-serif;display:flex;flex-direction:column;flex:1 1 0;height:auto;min-height:0;min-width:0;")}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:8px;min-width:0;padding-bottom:12px;')}>
        <span style={sx('flex:1 1 260px;min-width:0;display:flex;align-items:baseline;gap:11px;flex-wrap:wrap;row-gap:3px;')}>
          <span style={sx('flex:0 0 auto;font-size:22px;font-weight:700;letter-spacing:-.024em;line-height:1.2;color:#22272E;')}>Atlas</span>
          <span data-testid="atlas-role-line" style={sx('flex:1 1 180px;min-width:0;font-size:16px;color:#54606C;line-height:1.45;text-wrap:pretty;')}>{ctx.data?.roleLine ?? ''}</span>
        </span>
        <TabMore
          label="Atlas"
          rowStyle="flex:0 0 auto;display:flex;align-items:center;gap:4px;"
          tabs={tabs.map(([key, name]) => ({ key, label: name, name: key }))}
          keep={ATLAS_KEEP}
          value={tab}
          onChange={pick}
          tabStyle={atlasTab}
          before={railBtn && (
            <a
              href="#"
              aria-expanded={railOpen}
              onClick={(e) => {
                e.preventDefault();
                setRailPref(!railOpen);
              }}
              style={sx(
                'display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:13px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;margin-right:14px;border:1px solid rgba(34,39,46,.18);transition:background .18s ' +
                  EASE +
                  ';' +
                  (railOpen ? 'background:rgba(47,92,151,.12);color:#1D3F6B;border-color:rgba(47,92,151,.34);' : 'background:transparent;color:#54606C;'),
              )}
            >
              {railOpen ? 'Hide reach' : 'Reach & memory'}
            </a>
          )}
        />
      </div>

      {tab === 'Chat' && <ChatTab ar={ar} ctx={ctx} railOpen={railOpen} W={W} onScribe={() => pick('Scribe')} />}
      {tab === 'Conversations' && (
        <div data-screen-label="Atlas · Conversations" style={sx('display:flex;flex-direction:column;min-width:0;')}>
          {ar === 'Partner' && <ReadOnlyNote />}
          <RichCompanion embedded lens={ar === 'Care Staff' ? 'care' : ar === 'Facility Admin' ? 'admin' : 'op'} />
        </div>
      )}
      {tab === 'Scribe' && <ScribeTab ar={ar} scopeName={ctx.data?.scope.name ?? null} />}
      {tab === 'Actions' && <ActionsTab ar={ar} proposals={proposals} />}
      {tab === 'Autonomy' && <AutonomyTab ar={ar} />}
      {tab === 'Eval' && <EvalTab />}
    </div>
  );
}
